/**
 * useRecentlyViewedCustomers — localStorage-backed recents list.
 * Phase 5.8k.
 *
 * Stored shape: `[{id, name, phone_e164, language, last_seen_at}, ...]`
 * Max length = 8. Most-recent first. Per-user keyed off the email.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const MAX = 8;
const KEY_PREFIX = "kippu_recent_customers";

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function write(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
    // Notify other tabs/components in the same tab
    window.dispatchEvent(new CustomEvent("kippu:recent-customers-updated", { detail: { key } }));
  } catch {
    /* localStorage quota or disabled */
  }
}

export function useRecentlyViewedCustomers() {
  const { user } = useAuth();
  const key = `${KEY_PREFIX}:${(user?.email || "anon").toLowerCase()}`;
  const [list, setList] = useState(() => read(key));

  // Sync when other components update the list
  useEffect(() => {
    const refresh = () => setList(read(key));
    window.addEventListener("kippu:recent-customers-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("kippu:recent-customers-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [key]);

  const push = (cust) => {
    if (!cust?.id) return;
    const entry = {
      id: cust.id,
      name: cust.name || "(no name)",
      phone_e164: cust.phone_e164 || cust.phone || "",
      language: cust.language || "",
      last_seen_at: new Date().toISOString(),
    };
    const current = read(key).filter((r) => r.id !== cust.id);
    const next = [entry, ...current].slice(0, MAX);
    write(key, next);
    setList(next);
  };

  const remove = (id) => {
    const next = read(key).filter((r) => r.id !== id);
    write(key, next);
    setList(next);
  };

  const clear = () => {
    write(key, []);
    setList([]);
  };

  return { list, push, remove, clear };
}
